import { useCallback, useEffect, useState } from "react";
import { MdMonetizationOn } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import { repositorioCentroCusto } from "../../sync/repositories/financeiro";

const AJUDA = {
  titulo: "Cadastro de Centros de Custo",
  descricao:
    "Centros de Custo representam áreas ou departamentos da fazenda (ex.: 'Administrativo', 'Produção', 'Logística') aos quais uma despesa pode ser atribuída. Servem para saber onde o dinheiro está sendo gasto, além da categoria financeira.",
  secoes: [
    {
      titulo: "Dados do Centro de Custo",
      campos: [
        {
          nome: "Nome do Centro de Custo",
          obrigatorio: true,
          explicacao: "Nome do departamento ou área para alocação de custos (ex.: 'Talhão Norte', 'Curral').",
        },
      ],
    },
  ],
  dicas: [
    "Um centro de custo com movimentações vinculadas não pode ser excluído — o botão de excluir fica desabilitado nesse caso.",
  ],
};

export default function GestaoCentroCusto() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [centros, setCentros] = useState([]);
  const [centrosFiltrados, setCentrosFiltrados] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  const [filtros, setFiltros] = useState({
    busca: ""
  });
  
  const [formData, setFormData] = useState({
    nome: "",
  });

  // Carregar centros de custo
  const carregarCentros = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioCentroCusto.listar();
      const dados = response.data.results || response.data;
      setCentros(Array.isArray(dados) ? dados : []);
      setCentrosFiltrados(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar centros de custo:", error);
      alert("Erro ao carregar lista de centros de custo");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarCentros();
  }, [carregarCentros]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validarFormulario = () => {
    if (!formData.nome.trim()) {
      alert("Preencha o nome do centro de custo.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    const payload = {
      nome: formData.nome,
    };

    try {
      if (editando) {
        await repositorioCentroCusto.atualizar(editandoId, payload);
        alert("Centro de custo atualizado com sucesso!");
        handleCancel();
        carregarCentros();
      } else {
        await repositorioCentroCusto.criar(payload);
        alert("Centro de custo cadastrado com sucesso!");
        handleCancel();
        carregarCentros();
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (centro) => {
    setFormData({
      nome: centro.nome || "",
    });
    setEditando(true);
    setEditandoId(centro.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovoCentro = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      nome: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este centro de custo? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioCentroCusto.excluir(id);
      alert("Centro de custo excluído com sucesso!");
      carregarCentros();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir centro de custo. Verifique se não há registros vinculados.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = Array.isArray(centros) ? [...centros] : [];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(c => 
        c.nome?.toLowerCase().includes(buscaLower) ||
        c.id?.toString().includes(buscaLower)
      );
    }
    
    setCentrosFiltrados(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "" });
    setCentrosFiltrados(Array.isArray(centros) ? centros : []);
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Centros de Custo <MdMonetizationOn size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoCentro}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Centro de Custo
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Centro de Custo" : "Cadastrar Novo Centro de Custo"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Código</label>
                    <input
                      type="text"
                      className="border border-gray-300 p-2 rounded bg-gray-100 text-gray-600"
                      value={editando ? editandoId : "Automático"}
                      readOnly
                    />
                    <span className="text-xs text-gray-500 mt-1">
                      Gerado automaticamente pelo sistema
                    </span>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Nome do Centro de Custo *</label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Ex: Administrativo, Produção, Logística, etc."
                      required
                    />
                    <span className="text-xs text-gray-500 mt-1">
                      Nome do departamento ou área para alocação de custos
                    </span>
                  </div>
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Código ou Nome</label>
                  <input
                    type="text"
                    placeholder="Digite o código ou nome do centro de custo..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Centros de Custo */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {centrosFiltrados.length} centro(s) de custo encontrado(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Centro de Custo</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Movimentações</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {centrosFiltrados.map((centro) => (
                        <tr key={centro.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{centro.id}</td>
                          <td className="p-3 text-sm font-medium">{centro.nome}</td>
                          <td className="p-3 text-sm">
                            <span className="font-medium">{centro.movimentacoes_count || 0}</span>
                            <span className="text-gray-500 text-xs ml-1">movimentações</span>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditar(centro)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleExcluir(centro.id)}
                                className={`px-3 py-1 rounded text-sm transition-colors ${
                                  centro.movimentacoes_count > 0
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-red-600 hover:bg-red-700 text-white"
                                }`}
                                title={centro.movimentacoes_count > 0 ? "Centro com movimentações não pode ser excluído" : "Excluir"}
                                disabled={centro.movimentacoes_count > 0}
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      
                      {centrosFiltrados.length === 0 && (
                        <tr>
                          <td colSpan="4" className="p-8 text-center text-gray-500">
                            {filtros.busca 
                              ? "Nenhum centro de custo encontrado com os filtros selecionados."
                              : "Nenhum centro de custo cadastrado."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}